/**
 * ボリュームディスカウント（人数帯ごとの累進の割引率）の試算・要約・断り。
 *
 * **画面の試算はここだけを通す。**請求額を決めるのは back（BillingBatchService）で、
 * ここは「保存する前に、いくらになるかを見せる」ためのもの。式は back と同じにしてある。
 *
 *   帯の単価     = 率が0なら定価そのまま。それ以外は floor(定価 × (100 − 率) ÷ 100)（円未満切り捨て）
 *   人数ぶんの額 = Σ（その帯に入る人数 × その帯の単価）
 *                  帯 i は fromSeat(i)〜fromSeat(i+1)−1、最後の帯は上限なし（累進）
 *
 * **金額はすべて整数円**（利用者 2026-09-27「ここ日本だからなー？小数ないぞ？」）。
 * 率も整数の %（0〜99）。
 *
 * 設計：C:\biz\claude\人数帯の割引率_設計_2026-09-27.md
 * 文：C:\biz\claude\_local_run\割引率\Gemini_依頼と答え_ボリュームディスカウントの画面と請求書の文_2026-09-27.md（V02・V03・V15〜V19、2回目の W02）
 *
 * どこから見えるか：
 *   ・「PREIKA運営」→「請求管理」→ 画面下部「プランの料金」の「人数割」の列と［人数割設定］の小窓
 *   ・「PREIKA運営」→「契約企業一覧」→ 会社の［管理］→「料金・割引」タブ
 *   ・左メニュー「請求・お支払い」の今月のご請求の内訳（帯の範囲の書き方）
 */

import {
  NEXT_TIERS_TEMPLATE,
  appliedSinceText as monthSinceText,
  billedMonthOf,
  entriesAfter,
  entryOfMonth,
  fillMonth
} from './billingMonth'

/** 帯は10本まで（back も11本以上を断る）。 */
export const MAX_TIERS = 10

const MIN_RATE = 0
const MAX_RATE = 99

/*
 * 人数の上限。**back は int に収まらない人数を V16 で断る**（VolumeDiscountTiers.wholeNumber）。
 * 画面が整数かどうかだけを見ていると、3000000000 などを送ってから V16 が返る（点検 13）。
 */
const MAX_SEAT = 2147483647

/*
 * 断りの文（V15〜V19）。**back も同じ文で断る。**一字も変えない。
 */
const RATE_IS_WRONG = '割引率は0〜99の範囲内で、整数（小数点なし）で入力してください。'
const SEAT_IS_WRONG = '人数は1以上の範囲内で、重複のない整数（小数点なし）で入力してください。'
const FIRST_IS_NOT_ONE = '最初の帯は1名から始まるように設定してください。'
const RATE_GOES_DOWN = '人数が増えるにつれて割引率が高くなる（または同等）ように設定してください。'
const TOO_MANY_TIERS = '設定できる帯は10本までです。'

/* 要約の文（V02・V03）。 */
const NO_DISCOUNT = '割引なし'
const SUMMARY_JOINT = ' / '

/**
 * 整数として読めれば数を、読めなければ null を返す。
 * 空欄は null（Number('') は 0 になるので、先に空欄を分ける）。
 * 「10.0」のように小数点以下が0だけの書き方は整数として読む（最低月額の欄と同じ）。
 */
export function integerOrNull (value) {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  if (text === '') return null
  const number = Number(text)
  return Number.isInteger(number) ? number : null
}

/**
 * 帯の並びの断り。**送る前に止める**（back も同じ文で断る）。問題が無ければ空文字。
 *
 * @param tiers [{ fromSeat, discountRate }]（画面の入力は文字列のまま渡してよい）
 */
export function tierProblem (tiers) {
  const list = Array.isArray(tiers) ? tiers : []
  // 0本は「最初が1名からでない」と同じ扱い（back の指示 4-B）。
  if (!list.length) return FIRST_IS_NOT_ONE

  const rates = list.map(tier => integerOrNull(tier && tier.discountRate))
  if (rates.some(rate => rate === null || rate < MIN_RATE || rate > MAX_RATE)) {
    return RATE_IS_WRONG
  }

  const seats = list.map(tier => integerOrNull(tier && tier.fromSeat))
  if (seats.some(seat => seat === null || seat < 1 || seat > MAX_SEAT)) return SEAT_IS_WRONG
  if (new Set(seats).size !== seats.length) return SEAT_IS_WRONG

  if (Math.min.apply(null, seats) !== 1) return FIRST_IS_NOT_ONE

  // **人数の昇順に並べたとき、率が下がってはいけない**（下がると累進が壊れる）。
  const ordered = seats
    .map((seat, index) => ({ seat, rate: rates[index] }))
    .sort((a, b) => a.seat - b.seat)
  for (let i = 1; i < ordered.length; i++) {
    if (ordered[i].rate < ordered[i - 1].rate) return RATE_GOES_DOWN
  }

  if (list.length > MAX_TIERS) return TOO_MANY_TIERS
  return ''
}

/**
 * 送る形・計算に使う形へそろえる：数にして、人数の昇順に並べる。
 * **断りを通ったものだけを渡す**（tierProblem が空のとき）。
 */
export function normalizeTiers (tiers) {
  return (Array.isArray(tiers) ? tiers : [])
    .map(tier => ({
      fromSeat: integerOrNull(tier.fromSeat),
      discountRate: integerOrNull(tier.discountRate)
    }))
    .sort((a, b) => a.fromSeat - b.fromSeat)
}

/**
 * 帯の単価。**率0なら定価そのまま。**それ以外は円未満を切り捨て（お客様に有利な側）。
 * 定価も率も整数なので、同じ定価なら何度計算しても同じ円になる。
 */
export function unitPrice (listPrice, discountRate) {
  const price = Number(listPrice)
  const rate = Number(discountRate)
  if (rate === 0) return price
  return Math.floor(price * (100 - rate) / 100)
}

/**
 * 人数ぶんの額（累進）。帯が無ければ 人数 × 定価。
 *
 * 帯 i に入る人数は fromSeat(i)〜fromSeat(i+1)−1 のうち、人数に届いているぶんだけ。
 * **上の帯へはみ出した人数だけ安くなる**ので、帯の境目で額が逆転しない（30名 28,000円 → 31名 28,800円）。
 */
export function seatsAmount (seats, tiers, listPrice) {
  const count = Number(seats)
  const price = Number(listPrice)
  const ordered = normalizeTiers(tiers)
  if (!ordered.length) return count * price

  return ordered.reduce((sum, tier, index) => {
    const next = ordered[index + 1]
    const last = next ? Math.min(count, next.fromSeat - 1) : count
    const inTier = Math.max(0, last - tier.fromSeat + 1)
    return sum + inTier * unitPrice(price, tier.discountRate)
  }, 0)
}

/**
 * 試算（V12）に出す2つの額。**どちらも基本料を含む。**
 *   amount     … 基本料 ＋ 帯で積み上げた人数ぶんの額
 *   listAmount … 基本料 ＋ 人数 × 定価（割引なしのとき）
 * 最低月額はここに入れない（会社ごとに当てる・当てないが違うので、プランの試算では決められない）。
 */
export function trialAmounts (seats, tiers, listPrice, basePrice) {
  const base = Number(basePrice)
  return {
    seats: Number(seats),
    amount: base + seatsAmount(seats, tiers, listPrice),
    listAmount: base + Number(seats) * Number(listPrice)
  }
}

/** 割引がある組か。**組が無い・率が全部0は「割引なし」**（back も当てない）。 */
export function hasDiscount (tiers) {
  return Array.isArray(tiers)
    && tiers.some(tier => Number(tier && tier.discountRate) > 0)
}

/**
 * 人数の書き方。**3桁区切り**（請求書 PDF の「1,001名〜」と同じ。点検 12）。
 * 帯の範囲と、お客様の帯の行の「{人数} 人 × ¥{単価}」の人数に使う。
 */
export function seatCountText (value) {
  return Number(value).toLocaleString('ja-JP')
}

/**
 * 帯の範囲の書き方（V02・V32 で共通）：上限のある帯は「{開始}〜{終了}名」、最後の帯は「{開始}名〜」。
 * toSeat が null（または無い）なら最後の帯。数は3桁区切り（「1,001〜2,000名」「1,001名〜」）。
 */
export function rangeText (fromSeat, toSeat) {
  return toSeat === null || toSeat === undefined
    ? seatCountText(fromSeat) + '名〜'
    : seatCountText(fromSeat) + '〜' + seatCountText(toSeat) + '名'
}

/**
 * 要約（V02）を帯ごとに分けたもの。割引が無ければ空の配列。
 * 画面は帯の切れ目でだけ折り返したいので、分けたまま渡す（つなぎは SUMMARY_JOINT）。
 */
export function summaryParts (tiers) {
  if (!hasDiscount(tiers)) return []
  const ordered = normalizeTiers(tiers)
  return ordered.map((tier, index) => {
    const next = ordered[index + 1]
    return rangeText(tier.fromSeat, next ? next.fromSeat - 1 : null)
      + '：' + tier.discountRate + '%OFF'
  })
}

/** 要約の文（R02／V03）。例「1〜10名：0%OFF / 11〜30名：10%OFF / 31名〜：20%OFF」。 */
export function summaryText (tiers) {
  const parts = summaryParts(tiers)
  return parts.length ? parts.join(SUMMARY_JOINT) : NO_DISCOUNT
}

/** 要約のつなぎ（画面で帯ごとに分けて描くとき）。 */
export const SUMMARY_SEPARATOR = SUMMARY_JOINT

/**
 * 「プランの料金」の要約の後ろに添える、今の組を何年何月分から使っているか
 * （M-C「（{年}年{月}月分〜適用）」。2026-09-30 に日付から月へ変えた）。
 * **割引なし（組が無い・率が全部0）のときと、日付が無いときは空文字**（添えない）。
 * 月の出し方は料金と同じ（billingMonth.js の billedMonthOf。月の1日ならその月、それ以外は翌月）。
 *
 * @param volumeDiscount 一覧の行の volumeDiscount（{ appliedOn: 'YYYY-MM-DD', tiers }）
 */
export function appliedSinceText (volumeDiscount) {
  const tiers = volumeDiscount && Array.isArray(volumeDiscount.tiers) ? volumeDiscount.tiers : null
  if (!hasDiscount(tiers) || !volumeDiscount.appliedOn) return ''
  return monthSinceText(volumeDiscount.appliedOn)
}

/**
 * 先の月の組を、要約の下に1行ずつ添える文（AM-4、NEXT_TIERS_TEMPLATE）の並び（2026-09-30 の見直し）。
 *
 * 一覧の行の volumeDiscountSchedule（月ごとの組）のうち、今月より後の月の組を全部。
 * **前の月と同じ組の月は並べない**（何も変わらない行になる）。**割引なしの組でも出す**
 * （前の月に割引があれば「その月から割引なし」になることが分かるように）。
 * 前は最初の1か月だけで、2つ目以降の月の組は画面のどこにも出なかった。
 *
 * @param schedule 一覧の行の volumeDiscountSchedule
 * @param thisMonthStart 今月の1日（YYYY-MM-01）
 */
export function nextTiersLines (schedule, thisMonthStart) {
  const current = entryOfMonth(schedule, thisMonthStart)
  let previous = summaryText(current && current.tiers)
  const lines = []
  entriesAfter(schedule, thisMonthStart).forEach(entry => {
    const summary = summaryText(entry && entry.tiers)
    const month = billedMonthOf(entry.appliedOn)
    if (!month || summary === previous) return
    lines.push(fillMonth(NEXT_TIERS_TEMPLATE, month).replace('{要約}', summary))
    previous = summary
  })
  return lines
}

/**
 * その月（1日、YYYY-MM-01）に使われる組を、入力の形（文字列・人数の昇順）にする。
 * 組が無ければ「1名から0%」の1行（割引なし）。［人数割設定］の小窓が、選んでいる適用請求月の
 * 組から始めるため（月を変えたらその月の組に入れ替える。2026-09-30 の見直し）。
 */
export function tiersOfMonth (schedule, monthStart) {
  const entry = entryOfMonth(schedule, monthStart)
  const tiers = entry && Array.isArray(entry.tiers) ? entry.tiers : []
  if (!tiers.length) return [{ fromSeat: '1', discountRate: '0' }]
  return tiers
    .slice()
    .sort((a, b) => Number(a.fromSeat) - Number(b.fromSeat))
    .map(tier => ({ fromSeat: String(tier.fromSeat), discountRate: String(tier.discountRate) }))
}

/** 3桁区切りの数（円の前に置く）。 */
export function amountText (value) {
  return Number(value).toLocaleString('ja-JP')
}
